/**
 * DevizProTab — AI-powered construction estimate generator
 * Uses AI to generate detailed work activities from natural language descriptions
 */
import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, Check, Edit, Trash2, Plus,
  FileSpreadsheet, RefreshCw, ArrowLeft, Save, MessageSquare,
  Zap, ChevronRight, History, X, FileUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DevizProTab({ companyId, onDevizCreated }) {
  const [step, setStep] = useState("input"); // input | chat | activities | done
  const [description, setDescription] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState(null);
  const [aiNotes, setAiNotes] = useState("");
  const [totalEstimate, setTotalEstimate] = useState("");
  const [beneficiar, setBeneficiar] = useState("");
  const [obiectiv, setObiectiv] = useState("");
  const [creating, setCreating] = useState(false);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualItem, setManualItem] = useState({ name: "", um: "mp", quantity: 1, materialPrice: 0, manoperaPrice: 0 });
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [antemLoading, setAntemLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Handler: upload una sau MAI MULTE antemăsurători (poze sau PDF) → extragere AI
  const handleAntemUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 10) {
      toast.error("Maxim 10 fișiere odată");
      return;
    }
    const tooBig = files.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) {
      toast.error(`${tooBig.name}: prea mare (max 10MB)`);
      return;
    }
    const totalSize = files.reduce((s, f) => s + f.size, 0);
    if (totalSize > 30 * 1024 * 1024) {
      toast.error("Total fișiere > 30MB. Reduce dimensiunea.");
      return;
    }
    setAntemLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      // POST → 202 cu job_id (backend procesează async pentru a evita timeout Cloudflare)
      const initRes = await axios.post(
        `${API}/devizpro/extract-antemasuratoare?company_id=${companyId}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      const jobId = initRes?.data?.job_id;
      if (!jobId) {
        // Fallback pentru backend vechi care returnează sincron
        const data = initRes.data;
        if (!data?.activities?.length) {
          toast.error("Nu am putut extrage activități. Încearcă cu poze mai clare.");
          return;
        }
        setActivities(data.activities);
        setAiNotes(data.notes || data.source_summary || "");
        setStep("activities");
        toast.success(`${data.count} activități extrase din ${files.length} fișier${files.length > 1 ? "e" : ""}!`);
        return;
      }

      // Polling status — până la 3 minute (90 × 2s = 180s)
      let finalResult = null;
      let finalError = null;
      for (let attempt = 0; attempt < 90; attempt++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const statusRes = await axios.get(`${API}/devizpro/extract-jobs/${jobId}`);
          const s = statusRes?.data?.status;
          if (s === "sent") {
            finalResult = statusRes?.data?.result;
            break;
          }
          if (s === "failed") {
            finalError = statusRes?.data?.error || "Eroare necunoscută";
            break;
          }
        } catch (pollErr) {
          console.warn("Poll error (retrying):", pollErr?.message);
        }
      }

      if (finalError) {
        toast.error(finalError);
        return;
      }
      if (!finalResult?.activities?.length) {
        toast.error("Nu am putut extrage activități. Încearcă cu poze mai clare sau reîncearcă.");
        return;
      }
      setActivities(finalResult.activities);
      setAiNotes(finalResult.notes || finalResult.source_summary || "");
      setStep("activities");
      toast.success(
        `${finalResult.count} activități extrase din ${files.length} fișier${files.length > 1 ? "e" : ""}! Verifică și editează.`
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.blocked_by_proforma) {
        toast.error(detail.message || "Credite blocate de proformă neachitată");
      } else {
        toast.error(typeof detail === "string" ? detail : "Eroare la extragere. Reîncearcă.");
      }
    } finally {
      setAntemLoading(false);
      e.target.value = "";
    }
  };

  // Încarcă istoricul prompturilor memorate pentru această firmă
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/devizpro/prompts`, { params: { company_id: companyId, limit: 12 } });
        if (!cancelled) setSavedPrompts(Array.isArray(r.data) ? r.data : []);
      } catch (err) {
        console.error("Nu s-au putut încărca prompturile salvate:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const refreshSavedPrompts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/devizpro/prompts`, { params: { company_id: companyId, limit: 12 } });
      setSavedPrompts(Array.isArray(r.data) ? r.data : []);
    } catch { /* ignore */ }
  }, [companyId]);

  const handleDeletePrompt = async (id, e) => {
    e?.stopPropagation();
    setSavedPrompts((prev) => prev.filter((p) => p.id !== id));
    try {
      await axios.delete(`${API}/devizpro/prompts/${id}`, { params: { company_id: companyId } });
    } catch (err) {
      console.error("Eroare la ștergere prompt:", err);
      refreshSavedPrompts();
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendToAI = useCallback(async (msg, sid, msgCount) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/devizpro/chat?company_id=${companyId}`, {
        message: msg,
        session_id: sid,
        message_count: msgCount || 1,
      });

      const data = res.data;
      setSessionId(data.session_id);

      setMessages((prev) => [...prev, { role: "assistant", text: data.response }]);

      if (data.has_activities && data.activities) {
        setActivities(data.activities.activities);
        setAiNotes(data.activities.notes || "");
        setTotalEstimate(data.activities.total_estimate || "");
        setStep("activities");
      }

      return data;
    } catch (error) {
      console.error("AI error:", error);
      toast.error(error.response?.data?.detail || "Eroare la comunicarea cu AI");
      return null;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleStartChat = async () => {
    if (!description.trim()) {
      toast.error("Descrieți lucrarea dorită");
      return;
    }
    setStep("chat");
    setUserMessageCount(1);
    setMessages([{ role: "user", text: description }]);
    // Memorează prompt-ul (fire-and-forget, nu blocăm flow-ul AI)
    axios.post(`${API}/devizpro/prompts`, { company_id: companyId, text: description })
      .then(() => refreshSavedPrompts())
      .catch(() => { /* ignore — nu afectăm generarea */ });
    await sendToAI(description, null, 1);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || loading) return;
    const msg = userInput;
    const newCount = userMessageCount + 1;
    setUserInput("");
    setUserMessageCount(newCount);
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    await sendToAI(msg, sessionId, newCount);
  };

  const handleRegenerate = async () => {
    setActivities(null);
    setStep("chat");
    const msg = "Regenerează lista de activități cu alte opțiuni sau prețuri diferite.";
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    await sendToAI(msg, sessionId);
  };

  const updateActivity = (index, field, value) => {
    setActivities((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeActivity = (index) => {
    setActivities((prev) => prev.filter((_, i) => i !== index));
  };

  const addEmptyActivity = () => {
    setActivities((prev) => [
      ...prev,
      { code: `MAN${prev.length + 1}`, name: "", um: "buc", quantity: 1, materialPrice: 0, manoperaPrice: 0, description: "Adăugat manual" },
    ]);
  };

  const handleAddManualItem = () => {
    if (!manualItem.name.trim()) {
      toast.error("Introduceți denumirea lucrării");
      return;
    }
    const newItem = {
      code: `MAN${(activities?.length || 0) + 1}`,
      name: manualItem.name,
      um: manualItem.um,
      quantity: parseFloat(manualItem.quantity) || 1,
      materialPrice: parseFloat(manualItem.materialPrice) || 0,
      manoperaPrice: parseFloat(manualItem.manoperaPrice) || 0,
      description: "Adăugat manual",
    };
    if (!activities) {
      setActivities([newItem]);
      if (step !== "activities") setStep("activities");
    } else {
      setActivities((prev) => [...prev, newItem]);
    }
    setManualItem({ name: "", um: "mp", quantity: 1, materialPrice: 0, manoperaPrice: 0 });
    setShowManualAdd(false);
    toast.success("Lucrare adăugată!");
  };

  const handleCreateDeviz = async () => {
    if (!activities?.length) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/devizpro/create-deviz?company_id=${companyId}`, {
        activities,
        beneficiar,
        obiectiv: obiectiv || description.substring(0, 100),
      });
      toast.success(res.data.message);
      setStep("done");
      if (onDevizCreated) onDevizCreated(res.data.deviz);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = "Eroare la crearea devizului";
      if (typeof detail === "string") {
        msg = detail;
      } else if (detail && typeof detail === "object") {
        if (detail.message) {
          msg = detail.message;
          if (detail.required != null && detail.available != null) {
            msg += ` (necesari: ${detail.required}, disponibili: ${detail.available})`;
          }
        }
      }
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const totalMaterial = activities?.reduce((s, a) => s + (a.quantity || 0) * (a.materialPrice || 0), 0) || 0;
  const totalManopera = activities?.reduce((s, a) => s + (a.quantity || 0) * (a.manoperaPrice || 0), 0) || 0;
  const totalDirect = totalMaterial + totalManopera;

  const resetAll = () => {
    setStep("input");
    setDescription("");
    setSessionId(null);
    setMessages([]);
    setActivities(null);
    setAiNotes("");
    setTotalEstimate("");
    setBeneficiar("");
    setObiectiv("");
    setUserMessageCount(0);
    setShowManualAdd(false);
    setManualItem({ name: "", um: "mp", quantity: 1, materialPrice: 0, manoperaPrice: 0 });
  };

  return (
    <div className="space-y-4" data-testid="devizpro-tab">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 border-l-4 border-l-amber-400">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  DevizePRO
                  <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold border-0">AI</Badge>
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">Descrie lucrarea și AI generează activitățile cu prețuri</p>
              </div>
            </div>
            {step !== "input" && (
              <Button variant="ghost" size="sm" onClick={resetAll} className="text-slate-500">
                <ArrowLeft className="w-4 h-4 mr-1" /> Lucrare nouă
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Step 1: Description Input */}
      {step === "input" && (
        <Card className="border border-amber-200/50 shadow-md">
          <CardContent className="pt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Descrieți lucrarea dorită
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex: Vreau să torn șapă semi-umedă într-o casă de 100 mp, cu elicopterizare. Camera are 3m înălțime, pereți din BCA..."
                rows={4}
                className="resize-none border-amber-200 focus:border-amber-400 focus:ring-amber-400/20"
                data-testid="devizpro-description-input"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Includeți cât mai multe detalii: suprafață, materiale dorite, tip lucrare, condiții speciale
              </p>
            </div>

            {/* Istoric prompturi memorate */}
            {savedPrompts.length > 0 && (
              <div data-testid="devizpro-saved-prompts">
                <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-2">
                  <History className="w-3.5 h-3.5 text-amber-600" />
                  Prompturi salvate ({savedPrompts.length})
                  <span className="text-[10px] text-slate-400 font-normal ml-1">click pentru a reutiliza</span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                  {savedPrompts.map((p) => (
                    <div
                      key={p.id}
                      className="group inline-flex items-center gap-1 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full pl-2.5 pr-1 py-1 text-xs cursor-pointer transition-colors max-w-full"
                      onClick={() => setDescription(p.text)}
                      data-testid={`devizpro-prompt-${p.id}`}
                      title={p.text}
                    >
                      <span className="truncate max-w-[280px]">
                        {p.text.length > 60 ? `${p.text.slice(0, 60)}…` : p.text}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => handleDeletePrompt(p.id, e)}
                        className="opacity-60 hover:opacity-100 text-red-500 hover:text-red-700 rounded-full p-0.5"
                        title="Șterge din istoric"
                        data-testid={`devizpro-prompt-delete-${p.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={handleStartChat}
              disabled={!description.trim()}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
              data-testid="devizpro-start-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generează Activități cu AI
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
              <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">sau</span></div>
            </div>
            <Button
              variant="outline"
              onClick={() => { setActivities([]); setStep("activities"); }}
              className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="devizpro-manual-start-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Completează Manual Lucrările
            </Button>

            {/* Upload antemăsurătoare (UNA sau MAI MULTE poze/PDF-uri) → extragere automată cu AI */}
            <div className="relative">
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleAntemUpload}
                disabled={antemLoading}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
                data-testid="antem-file-input"
              />
              <Button
                variant="outline"
                disabled={antemLoading}
                className="w-full border-violet-200 text-violet-700 hover:bg-violet-50 pointer-events-none"
              >
                {antemLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extrag activitățile...</>
                ) : (
                  <><FileUp className="w-4 h-4 mr-2" /> Încarcă Antemăsurătoare (1-10 poze/PDF)</>
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center -mt-1">
              Poți selecta <strong>mai multe poze deodată</strong> (Ctrl/Cmd + click). AI le procesează pe toate într-un singur deviz. <strong>10 credite</strong> per extragere.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 2: AI Chat */}
      {step === "chat" && (
        <Card className="border border-amber-200/50 shadow-md">
          <CardContent className="pt-4 space-y-3">
            <ScrollArea className="h-[400px] pr-3">
              <div className="space-y-3">
                {messages.map((msg, i) => (
                  <div
                    key={`msg-${i}`}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                        msg.role === "user"
                          ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-br-md"
                          : "bg-slate-100 text-slate-700 rounded-bl-md"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <div className="whitespace-pre-wrap">{msg.text.replace(/```json[\s\S]*?```/g, "[Activități generate - vezi mai jos]")}</div>
                      ) : (
                        <div className="whitespace-pre-wrap">{msg.text}</div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                      <span className="text-sm text-slate-500">AI analizează...</span>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            <div className="flex gap-2">
              <Input
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                placeholder="Răspundeți întrebărilor AI..."
                disabled={loading}
                className="flex-1 border-amber-200"
                data-testid="devizpro-chat-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={loading || !userInput.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="devizpro-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Activities Review & Edit */}
      {step === "activities" && activities && (
        <div className="space-y-4">
          {/* Summary card */}
          <Card className="border border-green-200 bg-green-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                <Check className="w-5 h-5" />
                AI a generat {activities.length} activități
              </div>
              {aiNotes && <p className="text-xs text-slate-500 mb-2">{aiNotes}</p>}
              {totalEstimate && (
                <Badge variant="outline" className="border-green-300 text-green-700">{totalEstimate}</Badge>
              )}
            </CardContent>
          </Card>

          {/* Beneficiar & Obiectiv */}
          <Card className="border border-amber-200/50">
            <CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Beneficiar</label>
                <Input
                  value={beneficiar}
                  onChange={(e) => setBeneficiar(e.target.value)}
                  placeholder="Numele clientului"
                  className="mt-1 border-amber-200"
                  data-testid="devizpro-beneficiar"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Obiectiv / Lucrare</label>
                <Input
                  value={obiectiv}
                  onChange={(e) => setObiectiv(e.target.value)}
                  placeholder={description.substring(0, 60) + "..."}
                  className="mt-1 border-amber-200"
                  data-testid="devizpro-obiectiv"
                />
              </div>
            </CardContent>
          </Card>

          {/* Activities Table */}
          <Card className="border border-amber-200/50 shadow-md overflow-hidden">
            <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Activități Generate</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleRegenerate} className="text-xs border-amber-300">
                    <RefreshCw className="w-3 h-3 mr-1" /> Regenerează
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowManualAdd(!showManualAdd)}
                    className="text-xs border-amber-300 bg-amber-50"
                    data-testid="devizpro-add-manual-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Adaugă Lucrare
                  </Button>
                </div>
              </div>
            </CardHeader>

            {/* Manual Add Form */}
            {showManualAdd && (
              <div className="px-4 py-3 bg-amber-50/70 border-b border-amber-200">
                <p className="text-xs font-medium text-amber-700 mb-2">Adaugă lucrare manuală:</p>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
                  <div className="sm:col-span-2">
                    <label className="text-[10px] text-slate-500">Denumire</label>
                    <Input
                      value={manualItem.name}
                      onChange={(e) => setManualItem(p => ({ ...p, name: e.target.value }))}
                      placeholder="Ex: Montaj parchet"
                      className="h-8 text-xs"
                      data-testid="manual-item-name"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">UM</label>
                    <Input
                      value={manualItem.um}
                      onChange={(e) => setManualItem(p => ({ ...p, um: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">Cantitate</label>
                    <Input
                      type="number"
                      value={manualItem.quantity}
                      onChange={(e) => setManualItem(p => ({ ...p, quantity: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-500">Mat./UM</label>
                    <Input
                      type="number"
                      value={manualItem.materialPrice}
                      onChange={(e) => setManualItem(p => ({ ...p, materialPrice: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="flex gap-1">
                    <div className="flex-1">
                      <label className="text-[10px] text-slate-500">Man./UM</label>
                      <Input
                        type="number"
                        value={manualItem.manoperaPrice}
                        onChange={(e) => setManualItem(p => ({ ...p, manoperaPrice: e.target.value }))}
                        className="h-8 text-xs"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={handleAddManualItem}
                      className="h-8 mt-auto bg-amber-500 hover:bg-amber-600 text-white px-3"
                      data-testid="manual-item-add-btn"
                    >
                      <Check className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <CardContent className="p-0">
              {/* === DESKTOP / TABLET / LANDSCAPE: tabel === */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '40px' }} />
                    <col />
                    <col style={{ width: '72px' }} />
                    <col style={{ width: '90px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '44px' }} />
                  </colgroup>
                  <thead>
                    <tr className="bg-amber-50/50 text-left text-xs text-slate-500 border-b">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Activitate</th>
                      <th className="px-3 py-2">UM</th>
                      <th className="px-3 py-2 text-right">Cant.</th>
                      <th className="px-3 py-2 text-right">Mat./UM</th>
                      <th className="px-3 py-2 text-right">Man./UM</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((act, i) => {
                      const rowTotal = (act.quantity || 0) * ((act.materialPrice || 0) + (act.manoperaPrice || 0));
                      return (
                        <tr key={`act-${i}`} className="border-b border-amber-100 hover:bg-amber-50/30 align-top">
                          <td className="px-3 py-3 text-xs text-slate-400">{i + 1}</td>
                          <td className="px-3 py-1">
                            <Textarea
                              value={act.name}
                              onChange={(e) => updateActivity(i, "name", e.target.value)}
                              rows={2}
                              className="text-xs leading-relaxed border-transparent hover:border-amber-200 focus:border-amber-400 resize-y min-h-[44px] py-2 whitespace-pre-wrap break-words"
                              data-testid={`activity-name-${i}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              value={act.um}
                              onChange={(e) => updateActivity(i, "um", e.target.value)}
                              className="h-8 text-xs border-transparent hover:border-amber-200"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              value={act.quantity === 0 || act.quantity === '' || act.quantity == null ? '' : act.quantity}
                              onChange={(e) => updateActivity(i, "quantity", e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                              className="h-8 text-xs text-right border-transparent hover:border-amber-200"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              value={act.materialPrice === 0 || act.materialPrice === '' || act.materialPrice == null ? '' : act.materialPrice}
                              onChange={(e) => updateActivity(i, "materialPrice", e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                              className="h-8 text-xs text-right border-transparent hover:border-amber-200"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              value={act.manoperaPrice === 0 || act.manoperaPrice === '' || act.manoperaPrice == null ? '' : act.manoperaPrice}
                              onChange={(e) => updateActivity(i, "manoperaPrice", e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                              className="h-8 text-xs text-right border-transparent hover:border-amber-200"
                            />
                          </td>
                          <td className="px-3 py-3 text-right text-xs font-medium">
                            {rowTotal.toLocaleString("ro-RO", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-2 py-1">
                            <Button variant="ghost" size="sm" onClick={() => removeActivity(i)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-amber-50 border-t-2 border-amber-200 font-semibold text-xs">
                      <td colSpan={4} className="px-3 py-2 text-right">TOTAL:</td>
                      <td className="px-3 py-2 text-right text-blue-600">{totalMaterial.toLocaleString("ro-RO", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right text-green-600">{totalManopera.toLocaleString("ro-RO", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{totalDirect.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* === MOBILE PORTRAIT: layout cu carduri (un card per activitate) === */}
              <div className="md:hidden divide-y divide-amber-100">
                {activities.map((act, i) => {
                  const rowTotal = (act.quantity || 0) * ((act.materialPrice || 0) + (act.manoperaPrice || 0));
                  return (
                    <div key={`act-card-${i}`} className="p-3 bg-white" data-testid={`activity-card-${i}`}>
                      {/* Header: numărul + titlu + delete */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                          #{i + 1}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeActivity(i)}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                          data-testid={`activity-card-remove-${i}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>

                      {/* Numele activității — full width */}
                      <div className="mb-3">
                        <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Activitate</label>
                        <Textarea
                          value={act.name}
                          onChange={(e) => updateActivity(i, "name", e.target.value)}
                          rows={2}
                          className="text-sm leading-relaxed mt-1 border-amber-200 focus:border-amber-400 resize-y min-h-[56px] py-2 whitespace-pre-wrap break-words"
                          data-testid={`activity-card-name-${i}`}
                        />
                      </div>

                      {/* Linie 1: UM + Cantitate */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">UM</label>
                          <Input
                            value={act.um}
                            onChange={(e) => updateActivity(i, "um", e.target.value)}
                            className="h-9 text-sm mt-1 border-amber-200"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Cantitate</label>
                          <Input
                            type="number"
                            value={act.quantity === 0 || act.quantity === '' || act.quantity == null ? '' : act.quantity}
                            onChange={(e) => updateActivity(i, "quantity", e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                            className="h-9 text-sm text-right mt-1 border-amber-200"
                          />
                        </div>
                      </div>

                      {/* Linie 2: Mat/UM + Manoperă/UM */}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Mat./UM (RON)</label>
                          <Input
                            type="number"
                            value={act.materialPrice === 0 || act.materialPrice === '' || act.materialPrice == null ? '' : act.materialPrice}
                            onChange={(e) => updateActivity(i, "materialPrice", e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                            className="h-9 text-sm text-right mt-1 border-amber-200"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Man./UM (RON)</label>
                          <Input
                            type="number"
                            value={act.manoperaPrice === 0 || act.manoperaPrice === '' || act.manoperaPrice == null ? '' : act.manoperaPrice}
                            onChange={(e) => updateActivity(i, "manoperaPrice", e.target.value === '' ? '' : (parseFloat(e.target.value) || 0))}
                            className="h-9 text-sm text-right mt-1 border-amber-200"
                          />
                        </div>
                      </div>

                      {/* Total linie */}
                      <div className="flex items-center justify-between pt-2 mt-1 border-t border-amber-50">
                        <span className="text-xs text-slate-500">Total linie</span>
                        <span className="text-sm font-semibold text-amber-700">
                          {rowTotal.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Total general mobile */}
                <div className="p-3 bg-amber-50 border-t-2 border-amber-200">
                  <div className="grid grid-cols-2 gap-y-1.5 text-xs">
                    <span className="text-slate-600">Total Material:</span>
                    <span className="text-right font-semibold text-blue-600">
                      {totalMaterial.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                    </span>
                    <span className="text-slate-600">Total Manoperă:</span>
                    <span className="text-right font-semibold text-green-600">
                      {totalManopera.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                    </span>
                    <span className="text-slate-700 font-medium pt-1 border-t border-amber-200 mt-1">TOTAL GENERAL:</span>
                    <span className="text-right font-bold text-amber-700 pt-1 border-t border-amber-200 mt-1">
                      {totalDirect.toLocaleString("ro-RO", { minimumFractionDigits: 2 })} RON
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Create Deviz Button */}
          <Button
            onClick={handleCreateDeviz}
            disabled={creating || !activities.length}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg py-6 text-base"
            data-testid="devizpro-create-deviz-btn"
          >
            {creating ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Se creează devizul...</>
            ) : (
              <><FileSpreadsheet className="w-5 h-5 mr-2" /> Creează Deviz din Activitățile Generate</>
            )}
          </Button>
        </div>
      )}

      {/* Step 4: Done */}
      {step === "done" && (
        <Card className="border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <CardContent className="py-10 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-green-800">Deviz Creat cu Succes!</h3>
            <p className="text-sm text-green-600">Devizul a fost salvat și poate fi editat din lista de devize.</p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={resetAll} className="border-green-300 text-green-700">
                <Plus className="w-4 h-4 mr-2" /> Deviz PRO Nou
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
